"use strict";
var EventEmitter = require("eventemitter2").EventEmitter2;
var Set = require("es6-set");
var but = require("but");
var Promise = require("promise");
var par = require("par");

var ReactNative = require("react-native");

var MQTT = require("./nodesocket.js");
var createTracker = require("./tracker.js");

/**
 * Creates a new dispatcher instance
 * @param {Object} config The configuration for the dispatcher. If MQTT is enabled, it should contain `token`, `id`, and `host`
 * @param {Boolean} simple If this is set to true, MQTT is not going to be set up
 */
exports.createDispatcher = function createDispatcher(config) {
	var id = config.id;

	var host, token, mqtt, subscriptions, offline;
	var appStateListener;

	var match_mqtt = /^mqtt:(.*)/;
	var match_own_mqtt = /^mqtt:\$(.*)/;
	var match_incoming_mqtt = new RegExp("^u/" + id + "(/.*)");
	var match_published_mqtt = /^\$mqtt:(.*)/;

	var dispatcher = new EventEmitter({
		wildcard: "*",
		delimiter: ":",
		newListener: true,
		maxListeners: Infinity
	});

	dispatcher.createTracker = par(createTracker, dispatcher); // Simple way to create trackers for events, enables removal of multiple listeners in one go
	dispatcher.destroy = destroy; // Removes all listeners and destroys MQTT
	dispatcher.on("newListener", re_emit_new); // Re-emits newListener events with full event name

	if (!config.simple) setup_mqtt();

	return new Promise(handle_creation);

	/**
	 * Sets up MQTT socket and the related listeners
	 */
	function setup_mqtt() {
		host = config.host.replace(/^http/i, "ws");
		token = config.token;

		var clientId = token;

		if (config.quiet)
			clientId += "#Q";
		else if (config.instant)
			clientId += "#I";

		mqtt = MQTT.connect(host, {
			clientId: clientId,
			keepalive: 22,
			reconnectPeriod: 2000,
			tcphost: config.tcphost
		});
		subscriptions = new Set();

		dispatcher.mqtt = mqtt; // Just in case something requires raw access (hopefully not);

		mqtt.on("message", handle_mqtt_message);
		mqtt.on("connect", handle_mqtt_connect);
		mqtt.on("close", handle_mqtt_close);
		mqtt.on("error", console.warn.bind(console, "MQTT error:"));

		if(ReactNative.Platform.OS !== "web") {
			appStateListener = ReactNative.AppState.addEventListener("change", handle_focus);
		}

		dispatcher.on("$mqtt:*", handle_publish);
		dispatcher.on("removeListener", handle_removed);
		dispatcher.on("newListener", handle_new);
	}

	function handle_focus(state) {
		if(state === "active") {
			if (mqtt._checkPing) {
				mqtt._checkPing();
				setTimeout(function(){
					mqtt._checkPing();
				}, 300);
			}
		}
	}

	/**
	 * Used in the creation of a promise that resolves the first time the MQTT connection connects, and rejects the first time there's an error.
	 * @param  {Function} resolve The function to call to resolve the new promise
	 * @param  {Function} reject  The function to call to reject the new promise
	 */
	function handle_creation(resolve, reject) {
		if (config.simple) return resolve(dispatcher);

		mqtt.once("connect", on_connect);
		mqtt.once("error", on_error);

		function on_connect() {
			mqtt.removeListener("error", on_error);
			resolve(dispatcher);
		}

		function on_error(err) {
			console.log("MQTT creation error: ", err);
			mqtt.end();
			reject(err);
		}
	}

	/**
	 * Gets called whenever the mqtt connection connects or reconnects.
	 * This will re-subscribe to any subscriptions that it had before (reconnect),
	 * and then it will emit connectivity:online
	 */
	function handle_mqtt_connect() {
		// When connected iterate through subscriptions and re-subscribe.
		// `but()` is used to filter out the second and third argument in forEach
		// We bind subscribe to mqtt so that it will retain its `this` value when called as a callback
		subscriptions.forEach(but(mqtt.subscribe.bind(mqtt)));
		if(offline){
			offline = false;
			dispatcher.emit("connectivity:online");
		}
	}

	/**
	 * Handles the mqtt connection closing, emits connectivity:online
	 */
	function handle_mqtt_close() {
		if(!offline){
			offline = true;
			dispatcher.emit("connectivity:offline");
		}
	}

	/**
	 * Handles new mqtt messages received over the connection
	 * Emits to mqtt:{the topic}.
	 * If the topic is for the current user, it'll emit mqtt:${topic w/o u/:id}
	 * @param  {String} topic What topic got published to
	 * @param  {String} data  The published payload (hopefully JSON)
	 */
	function handle_mqtt_message(topic, data) {
		// Check to see if the topic was for the current user
		var match = match_incoming_mqtt.exec(topic);

		// Attempt to parse the payload
		var payload;
		try {
			payload = JSON.parse(data);
		} catch (e) {
			// If it wasn't JSON, warn the console and use the raw data
			console.warn("Got non-JSON payload from mqtt", topic, data);
			payload = data;
		}

		// If for current user, remove the u/:id from the topic
		if (match)
			dispatcher.emit("mqtt:" + ("$" + match[1]), payload);

		dispatcher.emit("mqtt:" + topic, payload);
	}

	/**
	 * Handles publishes with dispatcher.publish($mqtt:)
	 * If the topic starts with a $, u/:id is prepended to it in place of the $
	 * @param  {Object} data Payload to send as JSON
	 */
	function handle_publish(data) {
		data = data || {};
		var event = this.event;
		// Attempt to extract the topic with regex
		var match = match_published_mqtt.exec(event);

		// If there was a problem parsing, warn and exit
		if (!match) return console.warn("Invalid mqtt publish attempted:", event);

		var topic = match[1];

		// This replaces the $ at the beginning with u/:id if it exists
		if (topic[0] == "$") {
			topic = "u/" + id + topic.slice(1);
		}

		mqtt.publish(topic, JSON.stringify(data));
	}

	/**
	 * Handles new event listeners on the dispatcher.
	 * If its an mqtt event, subscribe on the connection
	 * @param  {String} event The event that's being subscribed to
	 */
	function handle_new(event) {
		var match = match_own_mqtt.exec(event);
		if (match)
			return subscribe_self(match[1]);
		match = match_mqtt.exec(event);
		if (match)
			return subscribe_to(match[1]);
	}

	function re_emit_new(event) {
		dispatcher.emit("newlistener:" + event);
	}

	/**
	 * Handles listeners being removed from the dispatcher
	 * If its an mqtt event, it is unsubscribed from
	 * @param  {String} event The even thats being un-listened on
	 */
	function handle_removed(event) {
		var match = match_own_mqtt.exec(event);

		// See if it's an mqtt event starting with $
		if (match)
			return unsubscribe_self(match[1]);

		match = match_mqtt.exec(event);
		if (match)
			return unsubscribe_to(match[1]);
	}

	/**
	 * Subscribes to a specific topic on mqtt
	 * If it's already in the subscriptions, it is ignored
	 * @param  {String} topic The topic to subscribe to
	 */
	function subscribe_to(topic) {
		if (subscriptions.has(topic)) return;
		subscriptions.add(topic);
		mqtt.subscribe(topic);
	}

	/**
	 * Used to wrap a topic with u/:id, to subscribe to "own" mqtt messages
	 * @param  {[type]} topic [description]
	 * @return {[type]}       [description]
	 */
	function subscribe_self(topic) {
		subscribe_to("u/" + id + topic);
	}

	/**
	 * Unsunbscribes from a topic if it has already been subscribed to
	 * @param  {[type]} topic [description]
	 */
	function unsubscribe_to(topic) {
		var amount_left = dispatcher.listeners(topic).length;
		if (amount_left) return;
		subscriptions.delete(topic);
		mqtt.unsubscribe(topic);
	}

	function unsubscribe_self(event) {
		unsubscribe_to("u/" + id + event);
	}

	/**
	 * Removes all listeners and closes the MQTT socket if one exists
	 */
	function destroy() {
		dispatcher.removeAllListeners();
		if (mqtt) {
			mqtt.end();
			if(ReactNative.Platform.OS !== "web" && appStateListener)
				appStateListener.remove();
		}
	}
};